import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, waLink } from "@/lib/api";
import { ArrowRight, Shield, ShieldCheck, Sparkles, Sun, Layers, Droplets, Wrench, Atom, Star, Play, MapPin, Award } from "lucide-react";

const iconMap = { shield: Shield, sparkles: Sparkles, sun: Sun, layers: Layers, droplets: Droplets, wrench: Wrench, atom: Atom };

const HERO = "https://images.unsplash.com/photo-1645400379459-f6fd3d963fd4?auto=format&fit=crop&w=1920&q=80";

export default function Home() {
  const [services, setServices] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative overflow-hidden noise-bg" data-testid="hero">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0C]/40 via-[#0A0A0C]/70 to-[#0A0A0C]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32">
          <div className="overline text-[#FFB800]">Tech Detailers · Premium Car Care Studio</div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl mt-4 max-w-3xl leading-[1.02]">
            Premium car care.
            <br /><span className="text-[#FFB800]">Zero compromise.</span>
          </h1>
          <p className="mt-6 max-w-xl text-zinc-300 text-base">
            PPF, ceramic, graphene, sun films, anti-rust and detailing —
            performed by certified technicians. Lifetime-warranty PPF films available on select packages.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/booking" className="btn-primary" data-testid="hero-cta-book">
              Book Now <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/quote" className="btn-outline" data-testid="hero-cta-quote">Get Instant Quote</Link>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="hero-cta-whatsapp">
              Chat on WhatsApp
            </a>
          </div>
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
            {[
              ["5★", "Google rating"],
              ["10,000+", "Vehicles protected"],
              ["Lifetime", "PPF warranty*"],
              ["Same-day", "Turnaround*"],
            ].map(([v, l]) => (
              <div key={l}>
                <div className="font-display text-2xl">{v}</div>
                <div className="overline mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Row */}
      <section className="border-y border-zinc-900 bg-[#0F0F12]" data-testid="trust-row">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <div className="flex items-start gap-3" data-testid="trust-garware">
            <ShieldCheck className="h-5 w-5 text-[#FFB800] mt-0.5 shrink-0" />
            <div>
              <div className="overline">Authorised</div>
              <div className="text-sm font-medium leading-tight mt-1">Garware Application Centre</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Star className="h-5 w-5 text-[#FFB800] fill-current mt-0.5 shrink-0" />
            <div>
              <div className="overline">Google</div>
              <div className="text-sm font-medium leading-tight mt-1">5★ verified reviews</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-[#FFB800] mt-0.5 shrink-0" />
            <div>
              <div className="overline">Studio</div>
              <div className="text-sm font-medium leading-tight mt-1">Navi Mumbai</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Award className="h-5 w-5 text-[#FFB800] mt-0.5 shrink-0" />
            <div>
              <div className="overline">Warranty</div>
              <div className="text-sm font-medium leading-tight mt-1">Up to lifetime · PPF films</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-7xl mx-auto px-6 py-24" data-testid="services-section">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-[#FFB800]">Our craft</div>
            <h2 className="font-display text-3xl sm:text-4xl mt-2">Every service, dialled in.</h2>
          </div>
          <Link to="/services" className="text-sm text-[#FFB800] hidden md:inline">All services →</Link>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => {
            const Icon = iconMap[s.icon] || Wrench;
            const spans = ["lg:col-span-2", "", "", "", "lg:col-span-2", "", ""];
            return (
              <Link key={s.id} to={`/quote?service=${s.id}`} data-testid={`service-card-${s.id}`}
                className={`card-surface rounded-lg p-7 ${spans[i] || ""}`}>
                <div className="flex items-center justify-between">
                  <Icon className="h-6 w-6 text-[#FFB800]" />
                  <ArrowRight className="h-4 w-4 text-zinc-500" />
                </div>
                <h3 className="font-display text-xl mt-6">{s.name}</h3>
                <div className="overline mt-1">{s.tagline}</div>
                <p className="text-sm text-zinc-400 mt-3 line-clamp-2">{s.description}</p>
                <div className="mt-5 text-xs text-zinc-500">
                  From <span className="text-[#F3F4F6] font-mono">₹{Math.min(...s.packages.map((p) => p.price)).toLocaleString()}</span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Live Job Preview */}
      <section className="max-w-7xl mx-auto px-6 py-16" data-testid="live-status-section">
        <div className="card-surface rounded-lg p-8 md:p-12 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-[#FFB800]">Live job status</div>
            <h2 className="font-display text-3xl mt-2">Watch your car through every stage.</h2>
            <p className="text-zinc-400 mt-4 text-sm">Check-in, wash, treatment, QC, delivery — real-time updates via WhatsApp and our tracker.</p>
            <Link to="/track" className="btn-outline mt-6 inline-flex" data-testid="live-track-cta">Track a job</Link>
          </div>
          <div className="grid gap-3">
            {["Check-in", "Wash & Prep", "Treatment", "Quality Check", "Delivery"].map((st, i) => (
              <div key={st} className={`flex items-center gap-3 px-4 py-3 rounded-md border border-zinc-800 ${i === 2 ? "stage-active" : ""}`}>
                <div className={`h-2 w-2 rounded-full ${i <= 2 ? "bg-[#FFB800]" : "bg-zinc-700"}`} />
                <div className="font-mono text-xs text-zinc-400">STAGE {i + 1}</div>
                <div className="text-sm">{st}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="max-w-7xl mx-auto px-6 py-20" data-testid="reviews-section">
        <div className="overline text-[#FFB800]">Word on the street</div>
        <h2 className="font-display text-3xl mt-2">What owners say.</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {reviews.map((r) => (
            <div key={r.id} className="card-surface rounded-lg p-6" data-testid={`review-${r.id}`}>
              <div className="flex gap-0.5 text-[#FFB800]">
                {Array.from({ length: r.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
              </div>
              <p className="text-sm text-zinc-300 mt-4">“{r.text}”</p>
              <div className="mt-5 text-xs">
                <div className="font-medium">{r.name}</div>
                <div className="text-zinc-500">{r.car}{r.source ? ` · ${r.source}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="rounded-lg border border-zinc-800 p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="cta-band">
          <div>
            <h3 className="font-display text-2xl md:text-3xl">Ready to protect your car?</h3>
            <p className="text-zinc-400 text-sm mt-2">Instant quote in under 60 seconds. No credit card needed.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/quote" className="btn-primary" data-testid="cta-quote-btn">Get Quote</Link>
            <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-outline" data-testid="cta-wa-btn">WhatsApp us</a>
          </div>
        </div>
      </section>
    </div>
  );
}
